import { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';
import { tokenizeContent, parseImetaMap } from '@/lib/contentParser';
import { useMentionName } from '@/hooks/useMentionName';

interface NoteContentProps {
  event: NostrEvent;
}

export function NoteContent({ event }: NoteContentProps) {
  const tokens = useMemo(() => tokenizeContent(event.content), [event.content]);
  const imeta = useMemo(() => parseImetaMap(event.tags), [event.tags]);

  // Collect image URLs that appear in imeta but not inline in content — append at end.
  const urlsInContent = useMemo(() => {
    const set = new Set<string>();
    for (const t of tokens) {
      if (t.type === 'image' || t.type === 'video' || t.type === 'link') set.add(t.url);
    }
    return set;
  }, [tokens]);

  const trailingMedia = useMemo(() => {
    const items: { url: string; kind: 'image' | 'video' }[] = [];
    for (const entry of imeta.values()) {
      if (urlsInContent.has(entry.url)) continue;
      const mime = entry.mime ?? '';
      if (mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|avif)($|\?)/i.test(entry.url)) {
        items.push({ url: entry.url, kind: 'image' });
      } else if (mime.startsWith('video/') || /\.(mp4|webm|mov|m4v)($|\?)/i.test(entry.url)) {
        items.push({ url: entry.url, kind: 'video' });
      }
    }
    return items;
  }, [imeta, urlsInContent]);

  return (
    <div className="whitespace-pre-wrap break-words text-foreground/90 leading-relaxed">
      {tokens.map((token, i) => {
        switch (token.type) {
          case 'text':
            return <Fragment key={i}>{token.value}</Fragment>;

          case 'image':
            return <RetroImage key={i} url={token.url} />;

          case 'video':
            return <RetroVideo key={i} url={token.url} />;

          case 'link':
            return (
              <a
                key={i}
                href={token.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-neon-cyan underline decoration-dotted underline-offset-4 hover:text-glow-cyan break-all"
              >
                {truncateUrl(token.url)}
              </a>
            );

          case 'mention':
            return <MentionLink key={i} pubkey={token.pubkey} raw={token.raw} />;

          case 'hashtag':
            return (
              <span
                key={i}
                className="text-neon-amber font-pixel text-[0.92em] px-1 hover:text-glow-amber"
              >
                #{token.tag}
              </span>
            );

          case 'nostr-ref':
            return (
              <span key={i} className="text-neon-green/70 text-[0.92em] font-mono">
                [{token.raw.slice(0, 14)}…]
              </span>
            );

          default:
            return null;
        }
      })}

      {trailingMedia.length > 0 && (
        <div className="mt-3 space-y-3">
          {trailingMedia.map((m) => (
            m.kind === 'image'
              ? <RetroImage key={m.url} url={m.url} />
              : <RetroVideo key={m.url} url={m.url} />
          ))}
        </div>
      )}
    </div>
  );
}

function RetroImage({ url }: { url: string }) {
  return (
    <span className="block my-3">
      <span className="block pixel-border-soft p-1 bg-black/60 relative overflow-hidden">
        <span className="absolute top-1 left-2 z-10 text-[10px] font-pixel text-neon-green/80 tracking-widest pointer-events-none">
          [IMG.LOAD]
        </span>
        <img
          src={url}
          alt=""
          loading="lazy"
          className="block w-full max-h-[520px] object-contain bg-black [image-rendering:auto]"
          onError={(e) => {
            const el = e.currentTarget;
            el.style.display = 'none';
            const parent = el.parentElement;
            if (parent && !parent.querySelector('.img-err')) {
              const err = document.createElement('span');
              err.className = 'img-err block p-6 text-center text-neon-red font-pixel text-xs';
              err.textContent = '> IMAGE.LOAD FAILED';
              parent.appendChild(err);
            }
          }}
        />
      </span>
    </span>
  );
}

function RetroVideo({ url }: { url: string }) {
  return (
    <span className="block my-3 pixel-border-soft p-1 bg-black/60">
      <video
        src={url}
        controls
        preload="metadata"
        className="block w-full max-h-[520px] bg-black"
      />
    </span>
  );
}

function MentionLink({ pubkey, raw }: { pubkey: string; raw: string }) {
  const name = useMentionName(pubkey);
  const npub = useMemo(() => {
    try {
      return raw.startsWith('npub1') ? raw : nip19.npubEncode(pubkey);
    } catch {
      return raw;
    }
  }, [pubkey, raw]);
  return (
    <Link
      to={`/${npub}`}
      className="text-neon-magenta font-pixel text-[0.92em] px-1 hover:text-glow-magenta"
    >
      @{name}
    </Link>
  );
}

function truncateUrl(url: string, max = 48): string {
  if (url.length <= max) return url;
  return url.slice(0, max - 1) + '…';
}
